import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
} from "@mui/material";
import { DialogHeader } from "../../../auth/components/DialogHeader";
import DatePickerLocalizationAdapter from "../../../main/context/DatePickerLocalizationAdapter";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { CardIntervalOptionsEnum } from "../../dashboard/types";
import { useContext, useEffect, useState } from "react";
import { usinasApi } from "../../../services/api";
import { MainContext } from "../../../main/context/MainContextProvider";
import { ErrorModal } from "../../../main/components/ErrorModal";
import { useNavigate } from "react-router-dom";
import CardBox from "../../../main/components/CardBox";
import CheckBoxTableRow from "../../users/components/CheckBoxTableRow";
import { Usina } from "../types";

export default function GenerateReportModal(props: {
    setOpen: (open: boolean) => void;
    open: boolean;
    interval: CardIntervalOptionsEnum
    onMount: () => void;
}) {
    const {
        toastNotifications,
        setToastNotifications,
        relatorioDiarioLoading,
        setRelatorioDiarioLoading,
        relatorioMensalLoading,
        setRelatorioMensalLoading,
    } = useContext(MainContext);

    const { interval, open, setOpen, onMount } =
        props;
    const onClose = () => setOpen(false);

    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs())

    const [errorModalData, setErrorModalData] = useState<{
        title: string;
        message: string;
    }>({ title: "", message: "" });

    const [loading, setLoading] = useState(true);

    const [usinas, setUsinas] = useState<Usina[]>([]);

    const [usinasIds, setUsinasIds] = useState<string[]>([])

    const mount = async () => {
        try {
            setLoading(true);
            const response = await usinasApi
                .getUsinas
                ();
            setUsinas(response.data);
            setUsinasIds(response.data.map((usina) => usina.usinaIds.id))
        } catch (error) {
            throw error;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        mount()
    }, [])

    useEffect(() => {
        if (interval == CardIntervalOptionsEnum.Dia) {
            setUsinas(usinas.filter((usina) => usina.reportOptions?.dailyReportEnabled))
        }
        else if (interval == CardIntervalOptionsEnum.Mes) {
            setUsinas(usinas.filter((usina) => usina.reportOptions?.monthlyReportEnabled))
        }
    }, [interval])

    const navigate = useNavigate();

    const getRelatorioDiario = async () => {
        if (!selectedDate) return;
        const reportMonth = selectedDate && selectedDate?.month() + 1;
        const dia = selectedDate.date() < 10 ? `0${selectedDate.date()}` : selectedDate.date();
        const mes = reportMonth < 10 ? `0${reportMonth}` : reportMonth;
        setRelatorioDiarioLoading(true);
        const newNotifications = {
            isAdding: true,
            notifications: [
                ...toastNotifications.notifications,
                {
                    severity: "info" as const,
                    text: "A busca foi inicializada, voce será notificado assim que ela for finalizada!",
                },
            ],
        };
        setToastNotifications(newNotifications);
        onClose();
        try {
            await usinasApi.generateDailyReport(
                `${selectedDate.year()}-${mes}-${dia}`,
                usinasIds
            );
            const newNotifications = {
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: "success" as const,
                        text: (
                            <>
                                Relatório do dia {`${dia}/${mes}/${selectedDate.year()}`} foi gerado com sucesso!{" "}
                                <a
                                    style={{ cursor: "pointer" }}
                                    onClick={() => navigate("/usinas/relatorios")}
                                >
                                    Clique aqui
                                </a>{" "}
                                para voltar a lista de relatórios.
                            </>
                        ),
                    },
                ],
            };
            setToastNotifications(newNotifications);

            onMount()
        } catch (error) {
            console.error(error)
        } finally {
            setRelatorioDiarioLoading(false);
        }
    };

    const getRelatorioMensal = async () => {
        if (!selectedDate) return;
        const reportMonth = selectedDate && selectedDate?.month() + 1;
        const mes = reportMonth < 10 ? `0${reportMonth}` : reportMonth;
        setRelatorioMensalLoading(true);
        const newNotifications = {
            isAdding: true,
            notifications: [
                ...toastNotifications.notifications,
                {
                    severity: "info" as const,
                    text: "A busca foi inicializada, voce será notificado assim que ela for finalizada!",
                },
            ],
        };
        setToastNotifications(newNotifications);
        onClose();
        try {
            await usinasApi.generateMonthlyReport(
                selectedDate.toDate(),
                usinasIds
            );

            const newNotifications = {
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: "success" as const,
                        text: (
                            <>
                                Relatório do mês {`${mes}/${selectedDate.year()}`} gerado com sucesso!{" "}
                                <a
                                    style={{ cursor: "pointer" }}
                                    onClick={() => navigate("/usinas/relatorios")}
                                >
                                    Clique aqui
                                </a>{" "}
                                para voltar a lista de relatórios.
                            </>
                        ),
                    },
                ],
            };
            setToastNotifications(newNotifications);

            onMount()
        } catch (error) {
            console.error(error)
        } finally {
            setRelatorioMensalLoading(false);
        }
    };

    const closeErrorModal = (open: boolean) => {
        !open && setErrorModalData({ title: "", message: "" });
    };

    const showErrorModal = errorModalData.title !== "";

    const removeUsinaId = (usinaId: string) => {
        const usinaIdFound = usinasIds.find(item => item === usinaId);
        if (usinaIdFound) {
            const newUsinasIds = usinasIds.filter(item => item !== usinaIdFound);
            setUsinasIds(newUsinasIds);
        }
    };

    return (
        <>
            <Dialog
                onClose={onClose}
                aria-labelledby="customized-dialog-title"
                open={open}
                sx={{
                    "& .MuiDialog-paper": {
                        width: "100%",
                        maxWidth: "400px",
                        maxHeight: "600px",
                    },
                }}
            >
                <DialogHeader id="customized-dialog-title" onClose={onClose}>
                    <Typography
                        sx={{
                            fontSize: {
                                xs: "1rem",
                                sm: "1.5rem",
                            },
                            fontWeight: 500,
                        }}
                    >
                        Geração de relatório
                    </Typography>
                </DialogHeader>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        overflowY: "auto",
                    }}
                    dividers
                >
                    {relatorioDiarioLoading || relatorioMensalLoading || loading ? (
                        <Box sx={{
                            textAlign: "center",
                            borderBottom: "none",
                            height: "100px",
                        }}>
                            <CircularProgress />
                        </Box>) : (
                        <>    <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                columnGap: "10px",
                                alignItems: "center",
                                width: "100%",
                                justifyContent: "space-between",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: "16px",
                                }}
                            >
                                Usinas
                            </Typography>
                            <Box
                                sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: "14px",
                                    }}
                                >
                                    Selecionar Todos
                                </Typography>
                                <CheckBoxTableRow
                                    active={usinas.length == usinasIds.length}
                                    onChange={(_, checked) =>
                                        checked ? setUsinasIds(usinas.map((usina) => usina.usinaIds.id)) : setUsinasIds([])
                                    }
                                />
                            </Box>
                        </Box>
                            <CardBox
                                sx={{
                                    width: "100%",
                                    height: "200px",
                                    maxHeight: "200px",
                                    overflow: "auto",
                                }}
                            >
                                <TableContainer
                                    sx={{ display: "flex", alignItems: "flex-start", height: "100%" }}
                                >
                                    <Table size="small" stickyHeader>
                                        <TableBody>
                                            {usinas.map((usina) => (
                                                <TableRow sx={{ display: "flex", width: "100%" }}>
                                                    <TableCell
                                                        sx={{
                                                            width: "100%",
                                                            display: "flex",
                                                            flexDirection: "row",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                        }}
                                                        align="left"
                                                    >
                                                        <Typography
                                                            sx={{
                                                                color: "#FFF",
                                                            }}
                                                        >
                                                            {usina.name ?? ""}
                                                        </Typography>
                                                        <Box
                                                            sx={{ display: "flex", flexDirection: "row", columnGap: "32px" }}
                                                        >
                                                            <CheckBoxTableRow
                                                                active={usinasIds.find(item => item === usina.usinaIds.id) ? true : false}
                                                                onChange={(_, checked) => {
                                                                    checked ?
                                                                        setUsinasIds([...usinasIds, usina.usinaIds.id]) :
                                                                        (removeUsinaId(usina.usinaIds.id));

                                                                }}
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardBox>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    width: "100%",
                                    marginTop: "18px",
                                }}
                            >
                                <DatePickerLocalizationAdapter>
                                    <DatePicker
                                        format={interval == CardIntervalOptionsEnum.Mes ? "MM/YYYY" : "DD/MM/YYYY"}
                                        views={interval == CardIntervalOptionsEnum.Mes ? ["month", "year"] : undefined}
                                        slotProps={{ textField: { size: "small" } }}
                                        maxDate={dayjs(new Date())}
                                        sx={{ marginRight: "8px" }}
                                        label="Data"
                                        value={selectedDate}
                                        onChange={(date) => {
                                            if (!date) return;
                                            setSelectedDate(date)
                                        }}
                                    />
                                </DatePickerLocalizationAdapter>
                            </Box></>)}
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={onClose}>
                        {"Cancelar"}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={relatorioDiarioLoading || relatorioMensalLoading || usinasIds.length === 0}
                        onClick={() => { interval == CardIntervalOptionsEnum.Dia ? getRelatorioDiario() : getRelatorioMensal() }}
                    >
                        {"Gerar relatório"}
                    </Button>
                </DialogActions>
            </Dialog>
            <ErrorModal
                contentText={errorModalData.message}
                open={showErrorModal}
                setOpen={closeErrorModal}
                title={errorModalData.title}
            />
        </>
    );
}